import type { Structure } from '../../types';
import { getReadinessLevel } from '../../utils/readiness';

export interface MarkerColorTokens {
  mapMarkerSelected: string;
  mapMarkerAvailable: string;
  neutral: string;
  risk: string;
  partial: string;
  progress: string;
}

/**
 * The property marker's ONE status channel: readiness.
 *
 * Shape says what a marker is (house = property, circle = observation,
 * triangle = before/after) and color says how far along that property is.
 * Keep them separate. If a future attribute needs to show on the map — help
 * wanted, wood available, review state — give it the corner badge or the
 * outline, not this fill, or the map stops being able to say either thing.
 *
 * `neutral` (claimed, nothing done yet) used to return `mapMarkerClaimed`,
 * which is byte-identical to `progress` — so a property whose owner had done
 * nothing looked exactly like one that was on track. colors.ts already
 * carried a `neutral` token described as "claimed but not started"; it just
 * was never wired up here.
 */
export function getStructureMarkerColor(
  structure: Structure,
  isSelected: boolean,
  colors: MarkerColorTokens
): string {
  if (isSelected) return colors.mapMarkerSelected;
  const readiness = getReadinessLevel(structure);
  if (readiness === 'progress') return colors.progress;
  if (readiness === 'partial') return colors.partial;
  if (readiness === 'risk') return colors.risk;
  return structure.claimSummary?.claimedBy ? colors.neutral : colors.mapMarkerAvailable;
}
